// Chart account detail: one account's identity plus bounded posted journal
// line that hits it. Read-only ledger drill-through; edits and the
// deactivate/reactivate lifecycle live on the settings page.

import { formatMoney } from "@sylviq/ui-core";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CardField } from "@/components/RecordCard";
import {
  type ChartAccount,
  getChartAccount,
  type JournalLine,
  listJournalLines,
} from "@/lib/api";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function plainType(accountType: string): string {
  const lower = accountType.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function statusTone(active: boolean): StatusTone {
  return active ? "success" : "idle";
}

export function ChartAccountDetailPage() {
  const { accountId } = useParams({ strict: false }) as { accountId: string };
  const [account, setAccount] = useState<ChartAccount | null>(null);
  const [lines, setLines] = useState<JournalLine[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linesError, setLinesError] = useState<string | null>(null);
  const [setupUnavailable, setSetupUnavailable] = useState(false);

  const loadAccount = useCallback(() => {
    if (!accountId) return;
    setError(null);
    getChartAccount(accountId)
      .then(setAccount)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, [accountId]);

  const loadLines = useCallback(() => {
    if (!accountId) return;
    setLinesError(null);
    setSetupUnavailable(false);
    listJournalLines(accountId)
      .then(setLines)
      .catch((cause: unknown) => {
        setLines(null);
        setSetupUnavailable(typeof cause === "object" && cause !== null && "status" in cause && cause.status === 404);
        setLinesError(toErrorMessage(cause));
      });
  }, [accountId]);

  useEffect(loadAccount, [loadAccount]);
  useEffect(loadLines, [loadLines]);

  // Running balance in natural (debit − credit) order. This is a movement
  // total, not a signed normal balance for the account type.
  const ledger = useMemo(() => {
    let running = 0;
    return (lines ?? []).map((line) => {
      running += line.debit_minor - line.credit_minor;
      return { line, running };
    });
  }, [lines]);

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/chart-of-accounts"
          search={(previous) => previous}
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Chart of accounts
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {account?.code ?? accountId}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={loadAccount}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {account === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : account ? (
        <>
          <PageHeader
            title={
              <>
                <span className="font-mono">{account.code}</span> — {account.name}
              </>
            }
            description={
              <>
                {plainType(account.account_type)}
                {account.sub_type ? ` · ${account.sub_type}` : ""} ·{" "}
                <StatusDot
                  tone={statusTone(account.active)}
                  label={account.active ? "Active" : "Inactive"}
                />
                {account.is_system ? (
                  <>
                    {" "}
                    · <Badge tone="neutral">Built-in</Badge>
                  </>
                ) : null}
              </>
            }
            actions={
              <>
                <Button asChild variant="secondary" size="sm">
                  <Link
                    to="/chart-of-accounts"
                    search={(previous) => previous}
                  >
                    Back to list
                  </Link>
                </Button>
                <Button asChild size="sm">
                  <Link
                    to="/chart-of-accounts/$accountId/settings"
                    params={{ accountId }}
                    search={(previous) => previous}
                  >
                    Edit
                  </Link>
                </Button>
              </>
            }
          />

          <section aria-label="Posted lines" className="grid gap-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-sm font-semibold">Ledger lines</h2>
              <p className="text-xs text-[var(--muted-foreground)]">
                Up to 500 posted lines, oldest first. Movement shown is debit minus credit, not the account balance.
              </p>
            </div>

            {lines && lines.length >= 500 ? (
              <p role="status" className="text-sm text-[var(--warning)]">
                The 500-line limit was reached. Later entries may not be shown; movement covers only the displayed lines.
              </p>
            ) : null}

            {linesError ? (
              <div
                role="alert"
                className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
              >
                Could not load ledger lines: {linesError}
                {setupUnavailable ? (
                  <p className="mt-2">
                    Accounting setup may not be complete, or this organization is no longer available. An authorized administrator can complete setup; reads do not enable the ledger automatically.{" "}
                    <Link to="/onboarding" search={(previous) => previous} className="underline">Open Accounting setup</Link>
                  </p>
                ) : null}
                <div className="mt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={loadLines}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
                  <div className="table-scroll">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Entry</TableHead>
                          <TableHead className="text-right">Debit</TableHead>
                          <TableHead className="text-right">Credit</TableHead>
                          <TableHead className="text-right">Movement shown</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lines === null ? (
                          <TableRow>
                            <TableCell colSpan={5}>Loading…</TableCell>
                          </TableRow>
                        ) : ledger.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5}>
                              No posted lines for this account yet.
                            </TableCell>
                          </TableRow>
                        ) : (
                          ledger.map(({ line, running }) => (
                            <TableRow key={line.line_id}>
                              <TableCell className="whitespace-nowrap">
                                {String(line.entry_date).slice(0, 10)}
                              </TableCell>
                              <TableCell>
                                <Link
                                  to="/journal/$entryId"
                                  params={{ entryId: line.entry_id }}
                                  className="underline-offset-4 hover:underline"
                                >
                                  {line.memo || line.entry_id}
                                </Link>
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {line.debit_minor > 0 ? (
                                  formatMoney({
                                    cents: line.debit_minor,
                                    currency: "USD",
                                  })
                                ) : (
                                  <span className="text-[var(--muted-foreground)]">
                                    —
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {line.credit_minor > 0 ? (
                                  formatMoney({
                                    cents: line.credit_minor,
                                    currency: "USD",
                                  })
                                ) : (
                                  <span className="text-[var(--muted-foreground)]">
                                    —
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {formatMoney({
                                  cents: running,
                                  currency: "USD",
                                })}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div className="stack-list grid gap-3 min-[64rem]:hidden">
                  {lines === null ? (
                    <p className="text-sm text-[var(--muted-foreground)]">
                      Loading…
                    </p>
                  ) : ledger.length === 0 ? (
                    <p className="text-sm text-[var(--muted-foreground)]">
                      No posted lines for this account yet.
                    </p>
                  ) : (
                    ledger.map(({ line, running }) => (
                      <article
                        key={line.line_id}
                        className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                      >
                        <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                          <span className="record-title font-medium">
                            {String(line.entry_date).slice(0, 10)}
                          </span>
                          <span className="tabular-nums">
                            <span className="sr-only">Movement shown: </span>
                            {formatMoney({ cents: running, currency: "USD" })}
                          </span>
                        </div>
                        <CardField label="Entry">
                          <Link
                            to="/journal/$entryId"
                            params={{ entryId: line.entry_id }}
                            className="underline-offset-4 hover:underline"
                          >
                            {line.memo || line.entry_id}
                          </Link>
                        </CardField>
                        <div className="grid grid-cols-2 gap-3">
                          <CardField label="Debit">
                            {line.debit_minor > 0
                              ? formatMoney({
                                  cents: line.debit_minor,
                                  currency: "USD",
                                })
                              : "—"}
                          </CardField>
                          <CardField label="Credit">
                            {line.credit_minor > 0
                              ? formatMoney({
                                  cents: line.credit_minor,
                                  currency: "USD",
                                })
                              : "—"}
                          </CardField>
                        </div>
                      </article>
                    ))
                  )}
                </div>
              </>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
